import {
  getUrlParameter,
  isMobileMode,
  isNativeOfflineShell,
} from 'helpers/core/url';

/**
 * sessionStorage key that records the env channel (prod|alpha) used
 * during the last authenticated WebClock load.  Used to detect an
 * environment-channel switch and trigger offline data cleanup.
 */
export const MOBILE_ENV_CHANNEL_KEY = 'WEBCLOCK_MOBILE_ENV_CHANNEL';

/**
 * Resolve the selected environment channel from the entry URL.
 *
 * Priority:
 *   1. `isProdEnvironment` query param ('true' | 'false')
 *   2. `appEnv` query param ('prod' | 'alpha')
 *   3. null when neither param is present
 *
 * Under Model A (single-env per channel), this does not select API URLs —
 * native serves the matching per-channel disk cache (`prod/active/` or
 * `alpha/active/`) which already contains the correct baked-in URLs.
 *
 * @returns {'prod'|'alpha'|null}
 */
const isProductionMobileMode = () =>
  isMobileMode() && process.env.NODE_ENV === 'production';

export const resolveMobileEnvChannel = () => {
  const isProdEnv = getUrlParameter('isProdEnvironment');
  if (isProdEnv === 'true') {
    return 'prod';
  }
  if (isProdEnv === 'false') {
    return 'alpha';
  }

  // In production mobile, only trust isProdEnvironment (set by native).
  if (isProductionMobileMode()) {
    return null;
  }

  const appEnv = getUrlParameter('appEnv');
  if (appEnv === 'prod') {
    return 'prod';
  }
  if (appEnv === 'alpha') {
    return 'alpha';
  }

  return null;
};

/**
 * Assert that native passed the env channel selector on an offline-shell load.
 * Throws when `offlineShell=true`, `NODE_ENV === 'production'`, and the
 * selector is missing. A hosted webview (`mobile=true` without
 * `offlineShell`) does not require the selector.
 *
 * Call during bootstrap before root.render (webClockBootstrap.js).
 *
 * @throws {Error}
 */
export const assertMobileRuntimeEnv = () => {
  if (!isNativeOfflineShell() || process.env.NODE_ENV !== 'production') {
    return;
  }

  const channel = resolveMobileEnvChannel();
  if (!channel) {
    throw new Error(
      '[WebClock] Mobile env selector missing. ' +
        "Pass 'isProdEnvironment=true' or 'isProdEnvironment=false' on the entry URL."
    );
  }
};

/**
 * Read the previously recorded mobile env channel from sessionStorage.
 * Returns null when no prior session key exists.
 *
 * @returns {'prod'|'alpha'|null}
 */
export const getLastMobileEnvChannel = () => {
  try {
    const value = window.sessionStorage.getItem(MOBILE_ENV_CHANNEL_KEY);
    return value === 'prod' || value === 'alpha' ? value : null;
  } catch {
    return null;
  }
};

/**
 * Persist the current env channel to sessionStorage so subsequent page
 * loads can detect a channel switch.
 *
 * @param {'prod'|'alpha'} channel
 */
export const setLastMobileEnvChannel = (channel) => {
  try {
    window.sessionStorage.setItem(MOBILE_ENV_CHANNEL_KEY, channel);
  } catch {
    // sessionStorage unavailable — silently skip
  }
};

/**
 * Resolve the prior env channel from the entry URL when native recreated
 * the WebView and sessionStorage does not retain the last channel.
 *
 * Native MUST pass `previousIsProdEnvironment` on the first load after a
 * channel-toggle re-login when the WebView may be recreated and sessionStorage
 * may be empty.
 *
 * @returns {'prod'|'alpha'|null}
 */
export const resolvePreviousMobileEnvChannel = () => {
  const previous = getUrlParameter('previousIsProdEnvironment');
  if (previous === 'true') {
    return 'prod';
  }
  if (previous === 'false') {
    return 'alpha';
  }
  return null;
};

/**
 * Returns true when the current URL's env channel differs from the last
 * persisted session channel, indicating a user just switched environments
 * (alpha ↔ prod) and re-logged in. A recorded session channel takes
 * precedence; `previousIsProdEnvironment` is consulted only when no session
 * channel is recorded.
 *
 * @returns {boolean}
 */
export const hasMobileEnvChannelChanged = () => {
  const current = resolveMobileEnvChannel();
  if (!current) {
    return false;
  }

  const last = getLastMobileEnvChannel();
  if (last !== null) {
    return last !== current;
  }

  const previousFromNative = resolvePreviousMobileEnvChannel();
  return previousFromNative !== null && previousFromNative !== current;
};

/**
 * Run channel-switch offline cleanup and update the session channel marker
 * only when cleanup succeeds (or no switch was detected).
 *
 * @param {() => Promise<boolean>} clearChannelData
 * @returns {Promise<{ channelChanged: boolean, cleanupSucceeded: boolean }>}
 */
export const runMobileEnvChannelSwitchCleanup = async (clearChannelData) => {
  const channelChanged = hasMobileEnvChannelChanged();
  let cleanupSucceeded = true;

  if (channelChanged) {
    cleanupSucceeded = await clearChannelData();
  }

  const currentChannel = resolveMobileEnvChannel();
  if (currentChannel && (!channelChanged || cleanupSucceeded)) {
    setLastMobileEnvChannel(currentChannel);
  }

  return { channelChanged, cleanupSucceeded };
};
